// ─── Export PDF d'un inventaire ───────────────────────────────────────────
// Deux variantes :
//   • 'recap'    : quantités actuelles affichées (pour partager / archiver).
//   • 'comptage' : feuille papier — quantité « système » en petit + colonne
//                  vierge « À compter » + case à cocher (inventaire physique).
// Options : photos (vignettes base64), catégories vides, propriétés.
//
// Moteur : HTML → expo-print (mobile, partage) / impression navigateur (web),
// même approche que services/menuPdf.js.
import { Platform } from 'react-native';
import { imprimerHTML } from '@mop/core';
import { disposerPlan, cellulesDe } from './plan';

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function qteItem(it) {
  if (Array.isArray(it.articles) && it.articles.length) return it.articles.reduce((a, x) => a + (Number(x.qte) || 0), 0);
  return Number(it.qte) || 0;
}

// Précharge les photos (1re photo par objet + photo de catégorie) en data-URI,
// indexées par URL. Nécessaire pour que l'impression/le PDF embarque les images.
async function urlEnDataURL(url) {
  try {
    const r = await fetch(url); const b = await r.blob();
    return await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(b); });
  } catch (_) { return null; }
}
// Photos d'un article (tableau) — supporte l'ancien champ unique `photo`.
function photosArt(a) {
  if (a && Array.isArray(a.photos) && a.photos.length) return a.photos;
  if (a && a.photo) return [a.photo];
  return [];
}
// Article principal d'un élément (modèle unifié : élément = ≥1 article).
function apP(it) { return (it && Array.isArray(it.articles) && it.articles.length) ? it.articles[0] : null; }
// Photos affichables d'un élément MONO (article principal) ou legacy (it.photos).
function photosMono(it) { const a = apP(it); return a ? photosArt(a) : (Array.isArray(it.photos) ? it.photos : []); }

export async function prechargerPhotosPdf(inv, items) {
  const map = {};
  const add = async (url) => { if (url && !map[url]) { const d = await urlEnDataURL(url); if (d) map[url] = d; } };
  for (const it of (items || [])) {
    const pm = photosMono(it);
    if (pm[0] && pm[0].url) await add(pm[0].url);
    for (const a of (it.articles || [])) { for (const ph of photosArt(a)) { if (ph && ph.url) await add(ph.url); } }
  }
  return map;
}

// Valeurs de propriétés remplies (à partir d'un objet `vals`) → « Nom : valeur · … ».
function propsLigne(vals, proprietes) {
  const out = [];
  for (const p of (proprietes || [])) {
    const v = vals && vals[p.id];
    if (v == null || v === '' || (Array.isArray(v) && !v.length)) continue;
    out.push(`${esc(p.nom)} : ${esc(Array.isArray(v) ? v.join(', ') : v)}`);
  }
  return out.join(' · ');
}

export function genererInventaireHTML({ inv, items, variante = 'recap', photos = false, categoriesVides = false, proprietes = true, nomCalendrier, photoMap = {}, emplacements = null, avecPlans = true }) {
  const arbre = (inv && inv.arbre) || [];
  const props = (inv && inv.proprietes) || [];
  const comptage = variante === 'comptage';

  // Chemin d'un emplacement (« Maison › Cuisine ») pour l'afficher sous l'article.
  // On privilégie l'arbre PARTAGÉ (avec les plans 2D) transmis, sinon repli sur inv.emplacements.
  const empArbre = (emplacements && emplacements.length) ? emplacements : ((inv && inv.emplacements) || []);
  const cheminEmp = (id) => {
    if (!id) return '';
    const path = [];
    const find = (list) => {
      for (const n of (list || [])) {
        path.push(n.nom);
        if (n.id === id) return true;
        if (find(n.enfants)) return true;
        path.pop();
      }
      return false;
    };
    return find(empArbre) ? path.join(' › ') : '';
  };
  const ligneEmp = (id) => { const c = cheminEmp(id); return c ? `<div class="pl">📍 ${esc(c)}</div>` : ''; };

  // Set de tous les id de catégories (pour repérer les objets « non classés »).
  const idsCat = new Set();
  (function marche(l) { for (const n of (l || [])) { idsCat.add(n.id); marche(n.enfants); } })(arbre);

  const itemsDe = (catId) => items.filter(it => it.catId === catId).sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
  const nonClasses = items.filter(it => !it.catId || !idsCat.has(it.catId));
  const aItems = (node) => (itemsDe(node.id).length > 0) || (node.enfants || []).some(aItems);

  const totalObjets = items.length;
  const totalPieces = items.reduce((a, it) => a + qteItem(it), 0);

  // Cellule photo (miniature) à partir d'une URL de photo (objet ou article).
  const cellVignette = (url) => {
    if (!photos) return '';
    const src = url ? (photoMap[url] || url) : null;   // web : URL directe ; mobile : base64
    return `<td class="cph">${src ? `<img src="${src}" class="th"/>` : ''}</td>`;
  };

  // Une ou plusieurs lignes <tr> pour un objet. Un objet « détaillé » (articles)
  // affiche une ligne-titre (quantité = somme) suivie d'une sous-ligne par article.
  const ligneItem = (it) => {
    const detaille = Array.isArray(it.articles) && it.articles.length > 1;   // multi = ≥2 articles
    const ap = apP(it);
    const pl = proprietes ? propsLigne(detaille ? (it.vals || {}) : (ap ? ap.vals : it.vals), props) : '';
    const pm = detaille ? [] : photosMono(it);
    const url0 = pm[0] && pm[0].url ? pm[0].url : null;
    const nomCell = `<td class="obj"><div class="nom">${esc(it.nom)}${detaille ? ` <span class="det">(${it.articles.length} article${it.articles.length > 1 ? 's' : ''})</span>` : ''}</div>${pl ? `<div class="pl">${pl}</div>` : ''}${!detaille && ap ? ligneEmp(ap.emplacementId) : ''}</td>`;

    let rows;
    if (comptage) {
      // Ligne-titre : total « système » ; on ne coche pas la ligne d'un objet détaillé
      // (le comptage se fait article par article).
      rows = `<tr>${cellVignette(url0)}${nomCell}<td class="qs">${qteItem(it)}</td><td class="cnt"></td><td class="chk">${detaille ? '' : '☐'}</td></tr>`;
    } else {
      const seuil = Number(it.seuil) || 0;
      rows = `<tr>${cellVignette(url0)}${nomCell}<td class="q">${qteItem(it)}</td><td class="s">${seuil > 0 ? seuil : ''}</td></tr>`;
    }

    if (detaille) {
      for (const a of it.articles) {
        const apl = proprietes ? propsLigne(a.vals, props) : '';
        const apm = photosArt(a);
        const aurl = apm[0] && apm[0].url ? apm[0].url : null;
        const artCell = `<td class="art"><div class="artnom">› ${esc(a.nom)}</div>${apl ? `<div class="pl">${apl}</div>` : ''}${ligneEmp(a.emplacementId)}</td>`;
        if (comptage) {
          rows += `<tr class="artrow">${cellVignette(aurl)}${artCell}<td class="qs">${Number(a.qte) || 0}</td><td class="cnt"></td><td class="chk">☐</td></tr>`;
        } else {
          rows += `<tr class="artrow">${cellVignette(aurl)}${artCell}<td class="q">${Number(a.qte) || 0}</td><td class="s"></td></tr>`;
        }
      }
    }
    return rows;
  };

  // En-tête de tableau selon la variante.
  const thead = comptage
    ? `<tr>${photos ? '<th class="cph"></th>' : ''}<th>Objet</th><th class="qs">Système</th><th class="cnt">À compter</th><th class="chk">✓</th></tr>`
    : `<tr>${photos ? '<th class="cph"></th>' : ''}<th>Objet</th><th class="q">Quantité</th><th class="s">Seuil</th></tr>`;

  // Rendu récursif d'une catégorie (+ sous-catégories).
  const renderCat = (node, niveau) => {
    if (!categoriesVides && !aItems(node)) return '';
    const propres = itemsDe(node.id);
    const titre = `${node.emoji || '📁'} ${esc(node.nom)}`;
    const bloc = `<div class="cat n${Math.min(niveau, 3)}">
      <div class="cattitre">${titre}</div>
      ${propres.length ? `<table class="grille"><thead>${thead}</thead><tbody>${propres.map(ligneItem).join('')}</tbody></table>` : '<div class="vide">—</div>'}
    </div>`;
    const enfants = (node.enfants || []).map(c => renderCat(c, niveau + 1)).join('');
    return bloc + enfants;
  };

  const corps = arbre.map(n => renderCat(n, 0)).join('');
  const ncBloc = nonClasses.length ? `<div class="cat n0"><div class="cattitre">📦 Non classé</div><table class="grille"><thead>${thead}</thead><tbody>${nonClasses.map(ligneItem).join('')}</tbody></table></div>` : '';

  // ── 2ᵉ partie : plans 2D des emplacements cités dans les localisations d'articles ──
  const citedDirect = new Set();
  for (const it of items) for (const a of ((it && it.articles) || [])) if (a && a.emplacementId) citedDirect.add(a.emplacementId);
  const toucheCite = (n) => citedDirect.has(n.id) || (n.enfants || []).some(toucheCite);
  const planSVG = (parent, surligne) => {
    const { cols, rows, place } = disposerPlan(parent, true);
    const placed = (place || []).filter(p => p.cells && p.cells.length);
    if (!placed.length) return '';
    const cs = 58, W = cols * cs, H = rows * cs;
    const contenu = placed.map(p => {
      const on = surligne.has(p.node.id);
      const cells = p.cells.map(c => `<rect x="${c.x * cs + 2}" y="${c.y * cs + 2}" width="${cs - 4}" height="${cs - 4}" rx="5" fill="${on ? '#e7e5fb' : '#f3f3f5'}" stroke="${on ? '#4b3fd6' : '#c9c9d2'}" stroke-width="${on ? '1.6' : '0.8'}"/>`).join('');
      const cx = (p.x + p.w / 2) * cs, cy = (p.y + p.h / 2) * cs;
      return `${cells}<text x="${cx}" y="${cy}" font-size="10" text-anchor="middle" dominant-baseline="middle" fill="#222">${esc(p.node.nom || '')}</text>`;
    }).join('');
    return `<div class="plan"><div class="plannom">${esc(parent.emoji || '📍')} ${esc(parent.nom || '')}</div><svg viewBox="0 0 ${W} ${H}" width="${Math.min(W, 480)}" preserveAspectRatio="xMinYMin meet" style="max-width:100%;height:auto;border:1px solid #eee;border-radius:8px">${contenu}</svg></div>`;
  };
  const plans = [];
  (function walk(liste) {
    for (const n of (liste || [])) {
      const enf = n.enfants || [];
      const aUnPlan = enf.some(c => cellulesDe(c));
      if (aUnPlan && toucheCite(n)) plans.push(planSVG(n, new Set(enf.filter(toucheCite).map(c => c.id))));
      walk(enf);
    }
  })(empArbre);
  const plansHtml = plans.filter(Boolean);
  const plansBloc = (avecPlans && plansHtml.length) ? `<h2 class="planssec">🗺 Plans des emplacements</h2><div class="plans">${plansHtml.join('')}</div>` : '';

  return `<!doctype html><html lang="fr"><head><meta charset="utf-8">
<title>${esc((inv && inv.nom) || 'Inventaire')}</title>
<style>
  @page { margin: 12mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111; font-size: 12px; }
  h1 { font-size: 21px; margin: 0 0 2px; }
  .sous { color: #555; margin: 0 0 14px; font-size: 12px; }
  .cat { page-break-inside: avoid; margin: 0 0 12px; }
  .cattitre { font-weight: 800; margin: 12px 0 4px; border-bottom: 2px solid #111; padding-bottom: 2px; }
  .cat.n0 .cattitre { font-size: 15px; }
  .cat.n1 .cattitre { font-size: 13.5px; margin-left: 10px; border-bottom: 1px solid #444; }
  .cat.n2 .cattitre, .cat.n3 .cattitre { font-size: 12.5px; margin-left: 20px; border-bottom: 1px dotted #888; }
  table.grille { width: 100%; border-collapse: collapse; page-break-inside: auto; margin: 2px 0 4px; }
  table.grille th, table.grille td { border: 0.5px solid #bbb; padding: 5px 7px; text-align: left; vertical-align: middle; }
  table.grille thead th { background: #cdd0d8; color: #1a1a1a; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; }
  tbody tr:nth-child(even) td { background: #f5f5f7; }
  .nom { font-weight: 600; }
  .det { color: #888; font-weight: 400; font-size: 10px; }
  .pl { color: #555; font-size: 10.5px; margin-top: 1px; }
  td.obj { padding-left: 12px; }
  td.art { padding-left: 30px; }
  .artnom { font-weight: 500; color: #333; font-size: 11.5px; }
  tbody tr.artrow td { background: #fbfbfc; border-top: 0.5px dashed #ccc; }
  .q, .qs, .s, .chk { text-align: center; width: 62px; white-space: nowrap; }
  .qs { color: #666; font-size: 11px; width: 58px; }
  .cnt { width: 90px; }
  .chk { width: 30px; font-size: 15px; }
  .cph { width: 40px; text-align: center; padding: 3px 4px; }
  img.th { width: 32px; height: 32px; object-fit: cover; border-radius: 4px; border: 0.5px solid #ccc; display: block; margin: 0 auto; }
  .vide { color: #999; margin: 2px 0 6px; }
  .planssec { font-size: 16px; margin: 22px 0 8px; border-top: 2px solid #111; padding-top: 10px; page-break-before: auto; }
  .plans { display: flex; flex-wrap: wrap; gap: 14px; }
  .plan { page-break-inside: avoid; }
  .plannom { font-weight: 700; font-size: 12px; margin: 0 0 3px; color: #4b3fd6; }
  footer { margin-top: 18px; color: #999; font-size: 10px; }
</style></head><body>
  <h1>${esc((inv && inv.emoji) || '📦')} ${esc((inv && inv.nom) || 'Inventaire')}</h1>
  <p class="sous">${nomCalendrier ? esc(nomCalendrier) + ' — ' : ''}${totalObjets} objet${totalObjets > 1 ? 's' : ''} · ${totalPieces} pièce${totalPieces > 1 ? 's' : ''}${comptage ? ' — feuille de comptage' : ''}</p>
  ${corps}
  ${ncBloc}
  ${(!corps && !ncBloc) ? '<p class="vide">Aucun élément.</p>' : ''}
  ${plansBloc}
  <footer>MoPCal</footer>
</body></html>`;
}

// Produit et partage/imprime le PDF. `params` = { inv, items, variante, photos,
// categoriesVides, proprietes, nomCalendrier }. Précharge les photos si demandé.
export async function exporterInventairePdf(params) {
  // Web : le navigateur charge les images distantes lui-même (URL directe) → pas
  // de préchargement (garde le geste utilisateur pour window.open). Mobile : on
  // embarque en base64 car expo-print ne charge pas les URL distantes.
  const photoMap = (params.photos && Platform.OS !== 'web') ? await prechargerPhotosPdf(params.inv, params.items) : {};
  const html = genererInventaireHTML({ ...params, photoMap });

  if (Platform.OS === 'web') return imprimerHTML(html);

  const Print = require('expo-print');
  const Sharing = require('expo-sharing');
  const { uri } = await Print.printToFileAsync({ html });
  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: (params.inv && params.inv.nom) || 'Inventaire', UTI: 'com.adobe.pdf' });
  }
  return true;
}
